import type React from 'react';
import { useMemo, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { HarborStatusType } from '@/types/harbor-management.types';
import type {
  IHarborData,
  HarborKYCData,
} from '@/types/harbor-management.types';
import { useGetHarborKYCQuery } from '@/services/HarborManagementService';
import { useUpdateFeaturedHarborMutation } from '@/services/HarborManagementService';
import { useUpdateKYCStatusMutation } from '@/services/ServiceProviderService';
import {
  useGetGuestBookingHarborOverrideQuery,
  useUpdateGuestBookingHarborOverrideMutation,
} from '@/services/GuestBookingGovernanceService';
import { GuestBookingOverride } from '@/types/GuestBookingGovernance.type';
import {
  AtSign,
  Briefcase,
  Building2,
  CalendarDays,
  Camera,
  Globe,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Sailboat,
  Ship,
  Star,
  UserCircle,
  Waves,
  Wrench,
  Calendar,
  Sun,
  Leaf,
  Snowflake,
  X,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/card/card';
import {
  formatFinnishCoordinate,
  formatFinnishNumber,
  formatFinnishPercentage,
  formatInternationalPhoneNumber,
} from '@/utils';
import { Modal } from '@/components/modal';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
// import { LegalAcceptanceCard } from '@/components/legal-acceptance/LegalAcceptanceCard';
import { HarborBookingType, KYCStatus, Statuses, UserRoles } from '@/constant';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useSnackbar } from 'notistack';
import { FacilityConditionIcon } from '@/components/FacilityConditionIcon';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { ISubscription } from '@/types/subscription-management.types';
import { SubscriptionPlansCard } from '@/components';
import EntityCancelPlanButton from '@/components/subscription-management/EntityCancelPlanButton';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  selectHarborCapabilities,
  setHarborCapabilities,
} from '@/store/harborCapabilitiesSlice';
import { HarborCapability } from '@/constant/harbour';
import { useGetCommissionPolicyByActorQuery } from '@/services/CommissionPolicyService';
import { ActorType, CommercialMode } from '@/types/commission-policy.types';
import DOMPurify from 'dompurify';
import { useDispatch } from 'react-redux';
import { formatDateWithOptions, formatDisplayDate } from '@/utils/Date';
import { useManagedListOptions } from '@/hooks/useManagedListOptions';
import { ManagedListType } from '@/types/list-value.types';

interface HarborDetailsViewProps {
  harbor: IHarborData;
}

const getHarborStatusLabel = (status: number | string | undefined) => {
  const numericStatus = Number(status);
  switch (numericStatus) {
    case HarborStatusType.ACTIVE:
      return 'Active';
    case HarborStatusType.INACTIVE:
      return 'Inactive';
    case HarborStatusType.DELETED:
      return 'Deleted';
    case HarborStatusType.PENDING_DELETION:
      return 'Pending Deletion';
    case HarborStatusType.UNDER_RENOVATION:
      return 'Under Renovation';
    default:
      return 'Unknown';
  }
};

export const HarborDetailsView: React.FC<HarborDetailsViewProps> = ({
  harbor,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const { getLabel: getHarborFacilityLabel } = useManagedListOptions(
    ManagedListType.HARBOR_FACILITY
  );
  const { getLabel: getHarborCategoryLabel } = useManagedListOptions(
    ManagedListType.HARBOR_CATEGORY
  );
  const { hasPermission } = usePermissions();

  const canReadKyc = hasPermission('kyc_management_read');
  const canUpdateFeatureListing = hasPermission('featured_listings_update');

  const canReadSubscriptions = hasPermission('subscription_management_read');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Featured toggle (for non-admin users on the detail page)
  const [updateFeaturedHarbor] = useUpdateFeaturedHarborMutation();
  const [confirmFeaturedState, setConfirmFeaturedState] = useState<{
    isOpen: boolean;
    featured: boolean;
  }>({ isOpen: false, featured: false });

  const handleToggleFeatured = async () => {
    if (!harbor?.id) return;
    try {
      await updateFeaturedHarbor({
        harborId: harbor.id,
        featured: confirmFeaturedState.featured,
      }).unwrap();
      enqueueSnackbar(
        intl.formatMessage(
          confirmFeaturedState.featured
            ? { id: 'HARBOR.FEATURED.TOAST_ADDED' }
            : { id: 'HARBOR.FEATURED.TOAST_REMOVED' }
        ),
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar(
        intl.formatMessage({ id: 'HARBOR.FEATURED.TOAST_ERROR' }),
        { variant: 'error' }
      );
    } finally {
      setConfirmFeaturedState({ isOpen: false, featured: false });
    }
  };

  // Guest booking override lives outside the harbor's own PATCH payload (it's
  // governed independently, see GuestBookingGovernanceService) so it's read and
  // saved through its own dedicated calls, not bundled into the harbor edit form.
  const canManageGuestBookingOverride = hasPermission('harbor_management_update');
  const { data: guestBookingOverrideData } = useGetGuestBookingHarborOverrideQuery(
    harbor?.id ?? '',
    { skip: !harbor?.id || !canManageGuestBookingOverride },
  );
  const [
    updateGuestBookingHarborOverride,
    { isLoading: isUpdatingGuestBookingOverride },
  ] = useUpdateGuestBookingHarborOverrideMutation();
  const [guestBookingOverride, setGuestBookingOverride] =
    useState<GuestBookingOverride>('inherit');

  useEffect(() => {
    setGuestBookingOverride(guestBookingOverrideData?.data?.override ?? 'inherit');
  }, [guestBookingOverrideData]);

  const handleGuestBookingOverrideChange = async (
    value: GuestBookingOverride,
  ) => {
    if (!harbor?.id) return;
    const previous = guestBookingOverride;
    setGuestBookingOverride(value);
    try {
      await updateGuestBookingHarborOverride({
        harborId: harbor.id,
        payload: { override: value },
      }).unwrap();
      enqueueSnackbar('Guest booking override saved.', {
        variant: 'success',
      });
    } catch (error: unknown) {
      setGuestBookingOverride(previous);
      const apiErr = error as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ?? 'Failed to save the guest booking override.',
        { variant: 'error' },
      );
    }
  };

  // Subscription plans state
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [associatedClubs, setAssociatedClubs] = useState<string[]>([]);
  const [subscriptionMessage, setSubscriptionMessage] = useState<string>('');
  const [planCancellationRequested, setPlanCancellationRequested] =
    useState(false);
  const [planSubscriptionEndDate, setPlanSubscriptionEndDate] = useState<
    string | null
  >(null);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);

  // KYC Update Status Modal state
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState<
    | KYCStatus.PENDING
    | KYCStatus.UNDER_REVIEW
    | KYCStatus.APPROVED
    | KYCStatus.REJECTED
    | KYCStatus.REQUIRES_UPDATE
    | KYCStatus.NOT_REQUIRED
    | KYCStatus.ON_HOLD
  >(KYCStatus.PENDING);
  const [kycNotes, setKycNotes] = useState<string>('');
  const [kycNotesError, setKycNotesError] = useState<string>('');
  const userData = useSelector((state: RootState) => state.auth.userData);

  const isLoggedInUserHarborAdmin = useMemo(() => {
    const loggedInUserId = userData?.id;
    const harborAdminId = (harbor as unknown as { manager?: { id?: string } })
      ?.manager?.id;
    return (
      !!loggedInUserId && !!harborAdminId && loggedInUserId === harborAdminId
    );
  }, [harbor, userData?.id]);

  const canAccessSubscriptions =
    canReadSubscriptions || isLoggedInUserHarborAdmin;

  const canReadCommissionPolicy = hasPermission(
    'commission_policy_management_read'
  );

  const actorId = harbor?.id ?? null;
  const { data: effectiveCommissionPolicy } =
    useGetCommissionPolicyByActorQuery(
      {
        actor_id: actorId,
        actor_type: ActorType.HARBOUR,
      },
      { skip: !actorId || !canReadCommissionPolicy }
    );

  const commissionSummary = useMemo(() => {
    if (!effectiveCommissionPolicy) return 'Not set';
    if (
      effectiveCommissionPolicy.commercial_mode ===
      CommercialMode.SUBSCRIPTION_ONLY
    ) {
      return 'Subscription only (0%)';
    }
    return `${formatFinnishPercentage(effectiveCommissionPolicy.commission_rate)} (${effectiveCommissionPolicy.commercial_mode})`;
  }, [effectiveCommissionPolicy]);

  // Get harbor capabilities from Redux (fetched when clicking harbor name)
  const harborCapabilities = useSelector(selectHarborCapabilities);

  // Check if DISCOVERY_MANAGE_BASIC_PROFILE capability is present
  const hasManageBasicProfileCapability = useMemo(() => {
    if (!harborCapabilities || harborCapabilities.length === 0) {
      return false;
    }
    return harborCapabilities.includes(
      HarborCapability.DISCOVERY_MANAGE_BASIC_PROFILE
    );
  }, [harborCapabilities]);

  // Check if the logged-in user is a site-level Admin (not a Marina/harbour role)
  const isSiteAdmin = useMemo(() => {
    const roles = (userData as unknown as { roles?: string[] })?.roles || [];
    return roles.includes(UserRoles.ADMIN);
  }, [userData]);

  // Feature listing on detail page is shown to non-admin users when capability is present.
  // Site admins use the harbour list toggle and the featured placement card on this page.
  const canFeatureListingInDetail = useMemo(() => {
    if (isSiteAdmin) return false;
    return (harborCapabilities ?? []).includes(
      HarborCapability.DISCOVERY_PROMOTED_PLACEMENT_BASIC
    );
  }, [isSiteAdmin, harborCapabilities]);

  // Fetch KYC data
  const {
    data: kycDataResponse,
    isLoading: kycLoading,
    error: kycError,
    refetch: refetchKYC,
  } = useGetHarborKYCQuery(harbor?.id || '', {
    skip: !harbor?.id || !canReadKyc,
  });

  // Handle both wrapped (ApiResponse) and direct response formats
  const kycDataRaw = useMemo(() => {
    if (!kycDataResponse) return null;

    // Check if response has a 'data' property (ApiResponse wrapper)
    // ApiResponse structure: { status: boolean, message?: string, data: T }
    const response = kycDataResponse as unknown as Record<string, unknown>;
    if (
      response?.data &&
      typeof response.data === 'object' &&
      response.data !== null
    ) {
      const data = response.data as Record<string, unknown>;
      // Check if data has KYC fields (id, entity_type, etc.)
      if (data.id && data.entity_type) {
        return data as unknown as HarborKYCData;
      }
    }

    // Check if response has KYC fields directly (id, entity_type, etc.)
    if (response?.id && response?.entity_type) {
      return response as unknown as HarborKYCData;
    }

    // Fallback: return null if structure doesn't match
    console.warn('Unexpected KYC response structure:', kycDataResponse);
    return null;
  }, [kycDataResponse]);

  // API returns plaintext account fields
  const kycData = kycDataRaw;

  // Update KYC Status mutation
  const [updateKYCStatus, { isLoading: isUpdatingKYC }] =
    useUpdateKYCStatusMutation();

  const dispatch = useDispatch();
  // Fetch harbor subscription plans and current plan
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setSubscriptionsLoading(true);

        // Fetch all harbor subscription plans
        const response = await SubscriptionsService.getSubscriptionsByType(
          'harbor',
          harbor?.id ? { harborId: harbor.id } : {}
        );
        setSubscriptions(response.subscriptions || []);

        // Fetch current plan for this harbor
        if (harbor?.id) {
          const currentPlan = await SubscriptionsService.getCurrentHarborPlan(
            harbor.id
          );
          setCurrentPlanId(currentPlan?.subscription?.id || null);
          setPlanCancellationRequested(
            !!currentPlan?.cancellation_requested_at
          );
          setPlanSubscriptionEndDate(
            currentPlan?.subscription_end_date ?? null
          );
          if (currentPlan?.subscription?.id) {
            // Fetch capabilities for the plan
            const capabilities =
              await SubscriptionsService.getSubscriptionCapabilities(
                currentPlan?.subscription?.id,
                harbor.id
              );

            // Extract unique keys and store in Redux
            const uniqueKeys =
              capabilities
                ?.filter((cap) => cap.include === true)
                .map((cap) => cap.unique_key) || [];

            dispatch(setHarborCapabilities({ capabilities: uniqueKeys }));
          } else {
            dispatch(setHarborCapabilities({ capabilities: [] }));
          }
          if (
            currentPlan?.associated_clubs &&
            currentPlan.associated_clubs.length > 0
          ) {
            setAssociatedClubs(currentPlan.associated_clubs);
            setSubscriptionMessage(currentPlan.message || '');
          } else {
            setAssociatedClubs([]);
            setSubscriptionMessage('');
          }
        }
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error);
        setSubscriptions([]);
        setCurrentPlanId(null);
        setAssociatedClubs([]);
        setSubscriptionMessage('');
      } finally {
        setSubscriptionsLoading(false);
      }
    };

    if (harbor?.id && canAccessSubscriptions) {
      fetchSubscriptions();
    } else if (!canAccessSubscriptions) {
      setSubscriptions([]);
      setSubscriptionsLoading(false);
      setCurrentPlanId(null);
      setAssociatedClubs([]);
      setSubscriptionMessage('');
    }
  }, [harbor?.id, canAccessSubscriptions, dispatch, subscriptionRefreshKey]);

  // Capabilities are now fetched when clicking harbor name/view icon
  // They are stored in Redux and will be available here via selectHarborCapabilities
  // No need to fetch capabilities here anymore

  const handleUpgrade = (planId: string) => {
    const selectedPlan = subscriptions.find((s) => s.id === planId);
    if (!selectedPlan || !harbor?.id) return;

    navigate('/subscription/payment', {
      state: {
        subscriptionId: planId,
        subscriptionName: selectedPlan.name,
        subscriptionPrice: selectedPlan.price,
        entityType: 'harbor',
        entityId: harbor.id,
        returnUrl: `/harbors/details`,
      },
    });
  };

  if (!harbor) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No harbor data available.</p>
      </div>
    );
  }

  const statusLabel = getHarborStatusLabel(harbor.status);

  const handleEditHarbor = () => {
    navigate(`/harbors/edit`, {
      state: { harbor, mode: 'edit', harborId: harbor.id },
    });
  };

  const currentSubscription =
    subscriptions.find((s) => s.id === currentPlanId) || null;

  return (
    <div className="harbor-page harbor-form container mx-auto px-4">
      {/* --- Header --- */}
      <header className="harbor-view-header relative mb-8 rounded-2xl bg-slate-800 p-8 text-white shadow-lg">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 rounded-2xl"
          style={{
            backgroundImage: `url('/placeholder.svg?width=1200&height=400')`,
          }}
        />
        <div className="relative z-10 flex flex-col items-start gap-6 md:flex-row md:items-center">
          {harbor.logo && (
            <img
              src={typeof harbor.logo === 'string' ? harbor.logo : undefined}
              alt={`${harbor.name} Logo`}
              className="harbor-logo h-24 w-24 rounded-full border-4 border-white  object-contain shadow-2xl"
            />
          )}
          <div className="flex-grow">
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
              {harbor.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {harbor.featured && (
                <div className="border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-yellow-400 text-yellow-900 hover:bg-yellow-500">
                  <Star className="mr-1.5 h-4 w-4" /> Featured
                </div>
              )}
              <div className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-blue-300 bg-blue-500/10 text-blue-200">
                {harbor.category
                  ? getHarborCategoryLabel(harbor.category)
                  : 'N/A'}
              </div>
              <div
                className={`${statusLabel === 'Active' ? 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80' : 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80'} inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`}
              >
                {statusLabel}
              </div>
              {harbor.country_short_code && (
                <div className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  {harbor.country_short_code}
                </div>
              )}
            </div>
          </div>
          {hasPermission('harbor_management_update') &&
            hasManageBasicProfileCapability && (
              <button
                className="bg-primary p-2 text-primary-foreground hover:bg-primary/80 inline-flex items-start rounded-full border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={handleEditHarbor}
                aria-label="Edit Harbor"
              >
                <Pencil className="h-6 w-6" />
              </button>
            )}
        </div>
      </header>

      {/* --- Main Content Grid --- */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* --- Left/Main Column --- */}
        <div className="space-y-8 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>About {harbor.name}</CardTitle>
            </CardHeader>
            <CardContent>
              {harbor.description ? (
                <div
                  className="text-gray-800 prose prose-strong:text-gray-800 prose-p:text-gray-800 prose-a:text-gray-800 prose-li:text-gray-800 prose-ul:text-gray-800 prose-ol:text-gray-800 prose-h1:text-gray-800 prose-h2:text-gray-800 prose-h3:text-gray-800 prose-h4:text-gray-800 prose-h5:text-gray-800 prose-h6:text-gray-800"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(harbor.description || ''),
                  }}
                />
              ) : (
                <p className="text-gray-700">No description available.</p>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-primary contrast:text-white" />{' '}
                  Facilities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {harbor.facilities_with_conditions?.length ? (
                    harbor.facilities_with_conditions.map(
                      (facilityWithCondition, i) => (
                        <div
                          className="text-foreground inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-gray-500"
                          key={i}
                        >
                          <FacilityConditionIcon
                            condition={facilityWithCondition.condition}
                            size="sm"
                          />
                          {getHarborFacilityLabel(
                            String(facilityWithCondition.facility)
                          )}
                        </div>
                      )
                    )
                  ) : harbor.facilities?.length ? (
                    harbor.facilities.map((f, i) => (
                      <div
                        className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-gray-500"
                        key={i}
                      >
                        {getHarborFacilityLabel(String(f))}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No facilities listed.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary contrast:text-white" />{' '}
                  Associated Club
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {harbor.club ? (
                    <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg border border-gray-200 w-full">
                      {harbor.club.logo && (
                        <img
                          src={harbor.club.logo}
                          alt={`${harbor.club.name} logo`}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {harbor.club?.name}
                        </h3>
                        <p className="text-xs text-gray-600 mt-1">
                          Associated Club
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No club associated
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary contrast:text-white" />{' '}
                Services
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {harbor.services?.length ? (
                  harbor.services.map((service) => (
                    <div
                      className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-gray-500"
                      key={service.id}
                    >
                      {service.name}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No services listed.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Seasonal Availability */}
          {harbor?.seasonality_support && harbor?.seasonal_dates && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary contrast:text-white" />{' '}
                  Seasonal Availability
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-green-300 dark:border-green-600 bg-green-500/10 dark:bg-green-500/20 text-green-700 dark:text-green-300 contrast:text-green-300">
                    <Sun className="mr-1.5 h-3 w-3" /> Seasonal Support Enabled
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    This harbor operates seasonally with specific opening dates
                    for each season.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Spring */}
                  {harbor?.seasonal_dates?.spring &&
                    harbor.seasonal_dates.spring.length > 0 && (
                      <div className="bg-gradient-to-br from-green-50 dark:from-transparent contrast:from-transparent to-green-100 dark:to-transparent contrast:to-transparent p-4 rounded-lg border border-green-200 dark:border-green-600 contrast:border-green-600">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-green-500 rounded-full">
                            <Leaf className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold text-green-800 dark:text-green-300 contrast:text-green-300">
                            Spring
                          </span>
                        </div>
                        <div className="space-y-3">
                          {harbor?.seasonal_dates?.spring?.map(
                            (range, index) => (
                              <div
                                key={index}
                                className="bg-green-50/50 dark:bg-transparent contrast:bg-transparent p-3 rounded-md border border-green-100 dark:border-green-600 contrast:border-green-600"
                              >
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-green-700 dark:text-green-300 contrast:text-green-300">
                                    Start Date:
                                  </span>
                                  <span className="text-sm font-semibold text-green-800 dark:text-green-300 contrast:text-green-300">
                                    {range.start_date
                                      ? formatDateWithOptions(
                                          new Date(range.start_date),
                                          {
                                            month: 'short',
                                            day: 'numeric',
                                          },
                                          'en-US'
                                        )
                                      : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-green-700 dark:text-green-300 contrast:text-green-300">
                                    End Date:
                                  </span>
                                  <span className="text-sm font-semibold text-green-800 dark:text-green-300 contrast:text-green-300">
                                    {range.end_date
                                      ? formatDateWithOptions(
                                          new Date(range.end_date),
                                          {
                                            month: 'short',
                                            day: 'numeric',
                                          },
                                          'en-US'
                                        )
                                      : 'N/A'}
                                  </span>
                                </div>
                                {range.is_closed && (
                                  <div className="mt-1 text-xs text-red-600 dark:text-red-300 contrast:text-red-300 font-medium">
                                    (Closed for this period)
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* Summer */}
                  {harbor?.seasonal_dates?.summer &&
                    harbor.seasonal_dates.summer?.length > 0 && (
                      <div className="bg-gradient-to-br from-yellow-50 dark:from-transparent contrast:from-transparent to-orange-100 dark:to-transparent contrast:to-transparent p-4 rounded-lg border border-yellow-200 dark:border-yellow-600 contrast:border-yellow-600">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-yellow-500 rounded-full">
                            <Sun className="w-4 h-4 text-white contrast:text-black" />
                          </div>
                          <span className="font-semibold text-yellow-800 dark:text-yellow-300 contrast:text-yellow-300">
                            Summer
                          </span>
                        </div>
                        <div className="space-y-3">
                          {harbor?.seasonal_dates?.summer?.map(
                            (range, index) => (
                              <div
                                key={index}
                                className="bg-yellow-50/50 dark:bg-transparent contrast:bg-transparent p-3 rounded-md border border-yellow-100 dark:border-yellow-600 contrast:border-yellow-600"
                              >
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300 contrast:text-yellow-300">
                                    Start Date:
                                  </span>
                                  <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 contrast:text-yellow-300">
                                    {range.start_date
                                      ? formatDateWithOptions(
                                          new Date(range.start_date),
                                          {
                                            month: 'short',
                                            day: 'numeric',
                                          },
                                          'en-US'
                                        )
                                      : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300 contrast:text-yellow-300">
                                    End Date:
                                  </span>
                                  <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 contrast:text-yellow-300">
                                    {range.end_date
                                      ? formatDateWithOptions(
                                          new Date(range.end_date),
                                          {
                                            month: 'short',
                                            day: 'numeric',
                                          },
                                          'en-US'
                                        )
                                      : 'N/A'}
                                  </span>
                                </div>
                                {range.is_closed && (
                                  <div className="mt-1 text-xs text-red-600 dark:text-red-300 contrast:text-red-300 font-medium">
                                    (Closed for this period)
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* Autumn */}
                  {harbor?.seasonal_dates?.autumn &&
                    harbor.seasonal_dates.autumn.length > 0 && (
                      <div className="bg-gradient-to-br from-orange-50 dark:from-transparent contrast:from-transparent to-red-100 dark:to-transparent contrast:to-transparent p-4 rounded-lg border border-orange-200 dark:border-orange-600 contrast:border-orange-600">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-orange-500 rounded-full">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 2a4 4 0 00-3.464 6h6.928A4 4 0 0010 2zM9 6a1 1 0 112 0 1 1 0 01-2 0zm-1 8a1 1 0 100-2 1 1 0 000 2z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <span className="font-semibold text-orange-800 dark:text-orange-300 contrast:text-orange-300">
                            Autumn
                          </span>
                        </div>
                        <div className="space-y-3 w-full">
                          {harbor.seasonal_dates.autumn.map((range, index) => (
                            <div
                              key={`autumn-${index}`}
                              className="bg-orange-50/50 dark:bg-transparent contrast:bg-transparent p-3 rounded-md border border-orange-100 dark:border-orange-600 contrast:border-orange-600"
                            >
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-orange-700 dark:text-orange-300 contrast:text-orange-300">
                                  Start Date:
                                </span>
                                <span className="text-sm font-semibold text-orange-800 dark:text-orange-300 contrast:text-orange-300">
                                  {range.start_date
                                    ? formatDateWithOptions(
                                        new Date(range.start_date),
                                        {
                                          month: 'short',
                                          day: 'numeric',
                                        },
                                        'en-US'
                                      )
                                    : 'N/A'}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-orange-700 dark:text-orange-300 contrast:text-orange-300">
                                  End Date:
                                </span>
                                <span className="text-sm font-semibold text-orange-800 dark:text-orange-300 contrast:text-orange-300">
                                  {range.end_date
                                    ? formatDateWithOptions(
                                        new Date(range.end_date),
                                        {
                                          month: 'short',
                                          day: 'numeric',
                                        },
                                        'en-US'
                                      )
                                    : 'N/A'}
                                </span>
                              </div>
                              {range.is_closed && (
                                <div className="mt-1 text-xs text-red-600 dark:text-red-300 contrast:text-red-300 font-medium">
                                  (Closed for this period)
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Winter */}
                  {harbor?.seasonal_dates?.winter &&
                    harbor.seasonal_dates.winter.length > 0 && (
                      <div className="bg-gradient-to-br from-blue-50 dark:from-transparent contrast:from-transparent to-cyan-100 dark:to-transparent contrast:to-transparent p-4 rounded-lg border border-blue-200 dark:border-blue-600 contrast:border-blue-600">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-blue-500 rounded-full">
                            <Snowflake className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold text-blue-800 dark:text-blue-300 contrast:text-blue-300">
                            Winter
                          </span>
                        </div>
                        <div className="space-y-3">
                          {harbor?.seasonal_dates?.winter?.map(
                            (range, index) => (
                              <div
                                key={`winter-${index}`}
                                className="bg-blue-50/50 dark:bg-transparent contrast:bg-transparent p-3 rounded-md border border-blue-100 dark:border-blue-600 contrast:border-blue-600"
                              >
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300 contrast:text-blue-300">
                                    Start Date:
                                  </span>
                                  <span className="text-sm font-semibold text-blue-800 dark:text-blue-300 contrast:text-blue-300">
                                    {range.start_date
                                      ? formatDateWithOptions(
                                          new Date(range.start_date),
                                          {
                                            month: 'short',
                                            day: 'numeric',
                                          },
                                          'en-US'
                                        )
                                      : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300 contrast:text-blue-300">
                                    End Date:
                                  </span>
                                  <span className="text-sm font-semibold text-blue-800 dark:text-blue-300 contrast:text-blue-300">
                                    {range.end_date
                                      ? formatDateWithOptions(
                                          new Date(range.end_date),
                                          {
                                            month: 'short',
                                            day: 'numeric',
                                          },
                                          'en-US'
                                        )
                                      : 'N/A'}
                                  </span>
                                </div>
                                {range.is_closed && (
                                  <div className="mt-1 text-xs text-red-600 dark:text-red-300 contrast:text-red-300 font-medium">
                                    (Closed for this period)
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>

                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/50 contrast:bg-blue-950/50 border border-blue-200 dark:border-blue-600 contrast:border-blue-600 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-300 contrast:text-blue-300 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-300 contrast:text-blue-300">
                        Seasonal Operation
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 contrast:text-blue-300 mt-1">
                        This harbor operates seasonally with specific opening
                        dates for each season. Outside these periods, the harbor
                        may be closed or have limited services.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Year-round Availability */}
          {!harbor.seasonality_support && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary contrast:text-white" />{' '}
                  Availability
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-200  rounded-lg">
                    <Calendar className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Year-round Availability
                    </p>
                    <p className="text-xs text-gray-600">
                      This harbor is available throughout the entire year.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {harbor.media && harbor.media.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-primary contrast:!text-white" />{' '}
                  Media Gallery
                </CardTitle>
              </CardHeader>
              <CardContent className="media-grid grid grid-cols-2 gap-4 md:grid-cols-3 px-6 pb-6">
                {harbor.media.map((mediaItem, idx) => {
                  const mediaUrl =
                    typeof mediaItem === 'string' ? mediaItem : '';
                  const isVideo = /\.(mp4|webm|ogg)$/i.test(mediaUrl); // You can extend this regex with more video formats

                  return isVideo ? (
                    <video
                      key={idx}
                      controls
                      className="aspect-video w-full rounded-lg object-cover transition-transform hover:scale-105"
                      data-testid={`video-${mediaUrl}`}
                    >
                      <source src={mediaUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      key={idx}
                      src={mediaUrl}
                      alt={`Media ${idx + 1}`}
                      className="aspect-video w-full rounded-lg object-cover transition-transform hover:scale-105"
                    />
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-primary contrast:!text-white" />{' '}
                  Media Gallery
                </CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="text-center text-gray-600">
                  Media content is currently unavailable.
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fairway Maps Section */}
          {(harbor.fairway_map?.length ||
            harbor.fairway_entrance_map?.length) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" /> Fairway Maps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 px-6 pb-6">
                {harbor.fairway_map && harbor.fairway_map.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Fairway Map</h3>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                      {harbor.fairway_map.map((map, idx) => (
                        <img
                          key={idx}
                          src={typeof map === 'string' ? map : ''}
                          alt={`Fairway Map ${idx + 1}`}
                          className="aspect-video w-full rounded-lg object-cover border border-gray-200"
                        />
                      ))}
                    </div>
                  </div>
                )}
                {harbor.fairway_entrance_map &&
                  harbor.fairway_entrance_map.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-3">
                        Fairway Entrance Map
                      </h3>
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                        {harbor.fairway_entrance_map.map((map, idx) => (
                          <img
                            key={idx}
                            src={typeof map === 'string' ? map : ''}
                            alt={`Fairway Entrance Map ${idx + 1}`}
                            className="aspect-video w-full rounded-lg object-cover border border-gray-200"
                          />
                        ))}
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          )}

          {/* Berth Image */}
          {harbor.berth_image && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-primary contrast:!text-white" />{' '}
                  Berth Image
                </CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <img
                  src={
                    typeof harbor.berth_image === 'string'
                      ? harbor.berth_image
                      : ''
                  }
                  alt="Berth"
                  className="w-full max-h-80 rounded-lg object-cover border border-gray-200"
                />
              </CardContent>
            </Card>
          )}
        </div>

        {/* --- Right/Sidebar Column --- */}
        <div className="space-y-8">
          {isSiteAdmin && hasPermission('harbor_management_update') && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold text-gray-900">
                  {intl.formatMessage({
                    id: 'HARBOR.FEATURED.PLATFORM_CARD_TITLE',
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium text-gray-800">
                    {intl.formatMessage({
                      id: 'HARBOR.FEATURED.IN_DISCOVERY',
                    })}
                  </span>
                  <label
                    className={`relative inline-flex items-center ${
                      harbor.status === Statuses.INACTIVE
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer'
                    }`}
                    title={
                      harbor.status === Statuses.INACTIVE
                        ? intl.formatMessage({
                            id: 'HARBOR.FEATURED.ACTIVE_REQUIRED',
                          })
                        : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!harbor.featured}
                      disabled={harbor.status === Statuses.INACTIVE}
                      onChange={() => {
                        if (harbor.status !== Statuses.INACTIVE) {
                          setConfirmFeaturedState({
                            isOpen: true,
                            featured: !harbor.featured,
                          });
                        }
                      }}
                      aria-label={intl.formatMessage(
                        {
                          id: 'HARBOR.FEATURED.ARIA_PLATFORM_TOGGLE',
                        },
                        { name: harbor.name }
                      )}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-blue-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" />
                    <span className="ml-3 text-sm font-semibold text-gray-700">
                      {harbor.featured
                        ? intl.formatMessage({ id: 'HARBOR.FEATURED.ON' })
                        : intl.formatMessage({ id: 'HARBOR.FEATURED.OFF' })}
                    </span>
                  </label>
                </div>
              </CardContent>
              <ConfirmationDialog
                open={confirmFeaturedState.isOpen}
                onClose={() =>
                  setConfirmFeaturedState({ isOpen: false, featured: false })
                }
                onConfirm={handleToggleFeatured}
                message={intl.formatMessage(
                  confirmFeaturedState.featured
                    ? { id: 'HARBOR.FEATURED.CONFIRM_ADD' }
                    : { id: 'HARBOR.FEATURED.CONFIRM_REMOVE' }
                )}
                cancelButtonText={intl.formatMessage({ id: 'COMMON.NO' })}
                confirmButtonText={intl.formatMessage({ id: 'COMMON.YES' })}
              />
            </Card>
          )}

          {/* --- Feature Listing Card (non-admin users with capability) --- */}
          {canFeatureListingInDetail && canUpdateFeatureListing && (
            <Card>
              <div className="flex items-center justify-between px-6 py-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {intl.formatMessage({
                      id: 'HARBOR.FEATURED.FEATURE_LISTING',
                    })}
                  </h2>
                </div>
                <div className="flex items-center gap-3">
                  <label
                    className={`relative inline-flex items-center ${
                      harbor.status === Statuses.INACTIVE ||
                      !canUpdateFeatureListing
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer'
                    }`}
                    title={
                      harbor.status === Statuses.INACTIVE
                        ? intl.formatMessage({
                            id: 'HARBOR.FEATURED.ACTIVE_REQUIRED',
                          })
                        : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!harbor.featured}
                      disabled={
                        harbor.status === Statuses.INACTIVE ||
                        !hasPermission('harbor_management_update')
                      }
                      onChange={() => {
                        if (
                          harbor.status !== Statuses.INACTIVE &&
                          hasPermission('harbor_management_update')
                        ) {
                          setConfirmFeaturedState({
                            isOpen: true,
                            featured: !harbor.featured,
                          });
                        }
                      }}
                      aria-label={intl.formatMessage(
                        {
                          id: 'HARBOR.FEATURED.ARIA_LISTING_TOGGLE',
                        },
                        { name: harbor.name }
                      )}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-blue-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" />
                    <span className="ml-3 text-sm font-semibold text-gray-700">
                      {harbor.featured
                        ? intl.formatMessage({ id: 'HARBOR.FEATURED.ON' })
                        : intl.formatMessage({ id: 'HARBOR.FEATURED.OFF' })}
                    </span>
                  </label>
                </div>
              </div>
              <ConfirmationDialog
                open={confirmFeaturedState.isOpen}
                onClose={() =>
                  setConfirmFeaturedState({ isOpen: false, featured: false })
                }
                onConfirm={handleToggleFeatured}
                message={intl.formatMessage(
                  confirmFeaturedState.featured
                    ? { id: 'HARBOR.FEATURED.CONFIRM_ADD' }
                    : { id: 'HARBOR.FEATURED.CONFIRM_REMOVE' }
                )}
                cancelButtonText={intl.formatMessage({ id: 'COMMON.NO' })}
                confirmButtonText={intl.formatMessage({ id: 'COMMON.YES' })}
              />
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Harbor Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-6 pb-6">
              <DetailItem
                icon={Ship}
                label={intl.formatMessage({ id: 'COMMON.BOOKING_TYPE' })}
                value={
                  harbor.booking_type === HarborBookingType.PAYMENT_ONLY
                    ? intl.formatMessage({ id: 'COMMON.PAYMENT_ONLY' })
                    : intl.formatMessage({ id: 'COMMON.RESERVATION' })
                }
              />
              <DetailItem
                icon={MapPin}
                label="Location"
                value={harbor.location}
              />
              <DetailItem
                icon={Globe}
                label="Coordinates"
                value={
                  harbor.latitude !== undefined &&
                  harbor.longitude !== undefined
                    ? `${formatFinnishCoordinate(
                        harbor.latitude
                      )}, ${formatFinnishCoordinate(harbor.longitude)}`
                    : null
                }
              />
              <DetailItem
                icon={CalendarDays}
                label="Check-in Time"
                value={harbor.default_check_in_time}
              />
              <DetailItem
                icon={CalendarDays}
                label="Check-out Time"
                value={harbor.default_check_out_time}
              />
              <DetailItem
                icon={CalendarDays}
                label="Check-in Window"
                value={
                  harbor.check_in_window_start && harbor.check_in_window_end
                    ? `${harbor.check_in_window_start} - ${harbor.check_in_window_end}`
                    : null
                }
              />
              <DetailItem
                icon={CalendarDays}
                label="Check-out Window"
                value={
                  harbor.check_out_window_start && harbor.check_out_window_end
                    ? `${harbor.check_out_window_start} - ${harbor.check_out_window_end}`
                    : null
                }
              />
              <DetailItem
                icon={Globe}
                label="Timezone"
                value={harbor.timezone}
              />
              <DetailItem
                icon={Ship}
                label="VHF Channel"
                value={harbor.vhf_channel}
              />
              <DetailItem
                icon={Sailboat}
                label="Berth Capacity"
                value={harbor.berth_capacity}
              />
              <DetailItem
                icon={Waves}
                label="Depth"
                value={
                  harbor.min_depth && harbor.max_depth
                    ? `${formatFinnishNumber(harbor.min_depth)} - ${formatFinnishNumber(harbor.max_depth)} ${harbor.depth_unit || ''}`
                    : null
                }
              />
              <DetailItem
                icon={Globe}
                label="Website"
                value={harbor.website_url}
                isLink
              />
              {harbor.terms_pdf && (
                <DetailItem
                  icon={FileText}
                  label="Terms"
                  value="Terms and Conditions"
                  isLink={
                    typeof harbor.terms_pdf === 'string'
                      ? harbor.terms_pdf
                      : undefined
                  }
                />
              )}
              {canReadCommissionPolicy && harbor.paytrail_merchant_id && (
                <DetailItem
                  icon={FileText}
                  label="Paytrail Merchant ID"
                  value={harbor.paytrail_merchant_id}
                />
              )}
            </CardContent>
          </Card>

          {/* <LegalAcceptanceCard summary={harbor.legal_acceptance_summary} /> */}

          <Card>
            <CardHeader>
              <CardTitle>Contact & Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-6 pb-6">
              <DetailItem
                icon={Phone}
                label="Phone"
                value={formatInternationalPhoneNumber(
                  harbor.contact_phone,
                  harbor.country_code,
                  harbor.country_short_code
                )}
              />
              <DetailItem
                icon={Mail}
                label="Email"
                value={harbor.contact_email}
                isLink={`mailto:${harbor.contact_email}`}
              />
              {harbor.manager && (
                <>
                  <DetailItem
                    icon={UserCircle}
                    label="Manager"
                    value={harbor.manager.name}
                  />
                  {harbor.manager.email && (
                    <DetailItem
                      icon={AtSign}
                      label="Manager Email"
                      value={harbor.manager.email}
                      isLink={`mailto:${harbor.manager.email}`}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>System Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-6 pb-6">
              <DetailItem
                icon={CalendarDays}
                label="Created On"
                value={
                  harbor.created_at &&
                  formatDisplayDate(new Date(harbor.created_at))
                }
              />
              <DetailItem
                icon={CalendarDays}
                label="Last Updated"
                value={
                  harbor.updated_at &&
                  formatDisplayDate(new Date(harbor.updated_at))
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Guest Booking Override */}
      {canManageGuestBookingOverride && (
        <div className="mt-8">
          <div className="card w-full max-w-full mx-auto">
            <div className="p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-6 w-6 flex-shrink-0 text-blue-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Guest booking
                    </h2>
                    <p className="text-sm text-gray-600">
                      Allow customers to book this harbor without signing in.
                      "Inherit" follows the platform-wide default set by the
                      platform admin.
                    </p>
                  </div>
                </div>
                <select
                  id="guest-booking-override-select"
                  className="select select-sm w-56"
                  value={guestBookingOverride}
                  disabled={isUpdatingGuestBookingOverride}
                  aria-label="Guest booking override"
                  onChange={(e) =>
                    handleGuestBookingOverrideChange(
                      e.target.value as GuestBookingOverride,
                    )
                  }
                >
                  <option value="inherit">Inherit platform default</option>
                  <option value="enabled">Enabled for this harbour</option>
                  <option value="disabled">Disabled for this harbour</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KYC Details Card - Simple Layout */}
      {canReadKyc && (
        <div className="mt-8">
          <div className="card w-full max-w-full mx-auto">
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  KYC Details
                </h3>
                {hasPermission('kyc_management_change_status') && (
                  <button
                    type="button"
                    onClick={() => {
                      setKycStatus(
                        (kycData?.kyc_status as
                          | KYCStatus.PENDING
                          | KYCStatus.UNDER_REVIEW
                          | KYCStatus.APPROVED
                          | KYCStatus.REJECTED
                          | KYCStatus.REQUIRES_UPDATE
                          | KYCStatus.NOT_REQUIRED
                          | KYCStatus.ON_HOLD) || KYCStatus.PENDING
                      );
                      setKycNotes(kycData?.admin_notes || '');
                      setKycNotesError('');
                      setIsUpdateStatusModalOpen(true);
                    }}
                    disabled={!kycData || kycLoading}
                    className="btn btn-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Update Status
                  </button>
                )}
              </div>

              {kycLoading ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-gray-500">Loading KYC data...</p>
                </div>
              ) : kycError ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-red-500">
                    Failed to load KYC data. Please try again later.
                  </p>
                </div>
              ) : !kycData ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-gray-500">
                    No KYC data available.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {kycData.legal_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Legal Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.legal_name}
                        </span>
                      </div>
                    )}
                    {kycData.business_id && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Business ID
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.business_id}
                        </span>
                      </div>
                    )}
                    {kycData.vat_id && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          VAT ID
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.vat_id}
                        </span>
                      </div>
                    )}
                    {kycData.address_line_1 && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Address Line 1
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.address_line_1}
                        </span>
                      </div>
                    )}
                    {kycData.address_line_2 && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Address Line 2
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.address_line_2}
                        </span>
                      </div>
                    )}
                    {kycData.postal_code && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Postal Code
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.postal_code}
                        </span>
                      </div>
                    )}
                    {kycData.city && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          City
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.city}
                        </span>
                      </div>
                    )}
                    {kycData.state && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          State
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.state}
                        </span>
                      </div>
                    )}
                    {kycData.country && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Country
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.country}
                        </span>
                      </div>
                    )}
                    {kycData.account_holder_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Account Holder Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all whitespace-normal overflow-hidden">
                          {kycData.account_holder_name}
                        </span>
                      </div>
                    )}
                    {kycData.account_number && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Account Number
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all whitespace-normal overflow-hidden">
                          {kycData.account_number}
                        </span>
                      </div>
                    )}
                    {kycData.iban && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          IBAN
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all md:break-normal">
                          {kycData.iban}
                        </span>
                      </div>
                    )}
                    {kycData.bic && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          BIC
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono">
                          {kycData.bic}
                        </span>
                      </div>
                    )}
                    {kycData.bank_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Bank Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.bank_name}
                        </span>
                      </div>
                    )}
                    {kycData.kyc_status && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          KYC Status
                        </span>
                        <span className="mt-1">
                          <span
                            className={`text-sm font-semibold capitalize ${
                              (kycData.kyc_status as string) ===
                              KYCStatus.APPROVED
                                ? 'text-green-600'
                                : (kycData.kyc_status as string) ===
                                    KYCStatus.REJECTED
                                  ? 'text-red-600'
                                  : (kycData.kyc_status as string) ===
                                        KYCStatus.UNDER_REVIEW ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.REQUIRES_UPDATE ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.ON_HOLD
                                    ? 'text-yellow-600'
                                    : 'text-gray-600'
                            }`}
                          >
                            {(kycData.kyc_status as string) ===
                            KYCStatus.PENDING
                              ? 'Pending'
                              : (kycData.kyc_status as string) ===
                                  KYCStatus.UNDER_REVIEW
                                ? 'Under Review'
                                : (kycData.kyc_status as string) ===
                                    KYCStatus.APPROVED
                                  ? 'Approved'
                                  : (kycData.kyc_status as string) ===
                                      KYCStatus.REJECTED
                                    ? 'Rejected'
                                    : (kycData.kyc_status as string) ===
                                        KYCStatus.REQUIRES_UPDATE
                                      ? 'Requires Update'
                                      : (kycData.kyc_status as string) ===
                                          KYCStatus.NOT_REQUIRED
                                        ? 'Not Required'
                                        : (kycData.kyc_status as string) ===
                                            KYCStatus.ON_HOLD
                                          ? 'On Hold'
                                          : (kycData.kyc_status as string) ===
                                              KYCStatus.RESUBMITTED
                                            ? 'Resubmitted'
                                            : kycData.kyc_status}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Admin Notes */}
                  {kycData.admin_notes && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-1">
                        Admin Notes
                      </h4>
                      <p className="text-sm text-gray-900 whitespace-pre-line">
                        {kycData.admin_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KYC Update Status Modal */}
      <Modal
        open={isUpdateStatusModalOpen}
        onClose={() => {
          setIsUpdateStatusModalOpen(false);
          setKycNotesError('');
        }}
        className="!flex items-center justify-center"
        aria-labelledby="update-kyc-status-title"
      >
        <div className="bg-gray-100  p-6 w-full max-w-md mx-auto rounded-lg shadow-xl">
          <div className="flex justify-between items-center mb-4">
            <h2
              id="update-kyc-status-title"
              className="text-lg font-semibold text-gray-900 dark:text-white contrast:text-white"
            >
              Update KYC Status
            </h2>
            <button
              type="button"
              onClick={() => {
                setIsUpdateStatusModalOpen(false);
                setKycNotesError('');
              }}
              className="text-gray-400 hover:text-gray-500 contrast:text-white  focus:outline-none"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="kyc-status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Status
              </label>
              <select
                id="kyc-status"
                value={kycStatus}
                onChange={(e) =>
                  setKycStatus(
                    e.target.value as
                      | KYCStatus.PENDING
                      | KYCStatus.UNDER_REVIEW
                      | KYCStatus.APPROVED
                      | KYCStatus.REJECTED
                      | KYCStatus.REQUIRES_UPDATE
                      | KYCStatus.NOT_REQUIRED
                      | KYCStatus.ON_HOLD
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-200 text-gray-900"
              >
                <option value={KYCStatus.PENDING}>Pending</option>
                <option value={KYCStatus.UNDER_REVIEW}>Under Review</option>
                <option value={KYCStatus.APPROVED}>Approved</option>
                <option value={KYCStatus.REJECTED}>Rejected</option>
                <option value={KYCStatus.NOT_REQUIRED}>Not Required</option>
                <option value={KYCStatus.REQUIRES_UPDATE}>
                  Requires Update
                </option>
                <option value={KYCStatus.ON_HOLD}>On Hold</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="kyc-notes"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                id="kyc-notes"
                value={kycNotes}
                onChange={(e) => {
                  setKycNotes(e.target.value);
                  if (e.target.value.trim()) {
                    setKycNotesError('');
                  }
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 resize-none text-gray-900 ${
                  kycNotesError
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
                }`}
                placeholder="Enter admin notes..."
              />
              {kycNotesError && (
                <p className="mt-1 text-xs text-red-500">{kycNotesError}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setIsUpdateStatusModalOpen(false);
                setKycNotesError('');
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 contrast:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 contrast:text-white hover:bg-gray-50 dark:hover:bg-gray-700 contrast:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!kycData?.id) {
                  console.error('KYC ID not available');
                  return;
                }

                if (!kycNotes.trim()) {
                  setKycNotesError('Notes are required');
                  return;
                }

                try {
                  await updateKYCStatus({
                    kycId: kycData.id,
                    entityId: harbor?.id || '',
                    data: {
                      kyc_status: kycStatus,
                      admin_notes: kycNotes,
                    },
                  }).unwrap();

                  // Refetch KYC data after successful update
                  await refetchKYC();

                  setIsUpdateStatusModalOpen(false);
                  // You can add a success notification here
                } catch (error) {
                  console.error('Failed to update KYC status:', error);
                  // You can add an error notification here
                }
              }}
              disabled={isUpdatingKYC || !kycData?.id}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 contrast:bg-primary contrast:hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingKYC ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      </Modal>

      {canReadCommissionPolicy && (
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Commercial Terms</span>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() =>
                    navigate(`/harbors/${harbor.id}/commission`, {
                      state: { from: `${location.pathname}${location.search}` },
                    })
                  }
                >
                  Commission Policy - Manage
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6 text-sm text-muted-foreground">
              Current commission: {commissionSummary}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Subscription Plans Section */}
      {canAccessSubscriptions && !isLoggedInUserHarborAdmin && (
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary contrast:text-white" />{' '}
                Harbor Subscription Plans
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {associatedClubs.length === 0 && harbor?.id && (
                <EntityCancelPlanButton
                  entityType="harbor"
                  entityId={harbor.id}
                  entityName={harbor.name}
                  planPrice={currentSubscription?.price}
                  cancellationRequested={planCancellationRequested}
                  subscriptionEndDate={planSubscriptionEndDate}
                  onSuccess={() =>
                    setSubscriptionRefreshKey((key) => key + 1)
                  }
                  className="mb-4"
                />
              )}
              {subscriptionsLoading ? (
                <SubscriptionPlansCard
                  subscriptions={
                    currentSubscription ? [currentSubscription] : []
                  }
                  loading={subscriptionsLoading}
                  currentPlanId={currentPlanId}
                />
              ) : associatedClubs.length > 0 ? (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">
                    {subscriptionMessage ||
                      'This harbor’s current subscription is associated with the following club(s)'}
                  </p>
                  <ul className="list-disc list-inside text-sm text-blue-800">
                    {associatedClubs.map((club, index) => (
                      <li key={index}>{club}</li>
                    ))}
                  </ul>
                </div>
              ) : currentSubscription ? (
                <SubscriptionPlansCard
                  subscriptions={[currentSubscription]}
                  loading={false}
                  currentPlanId={currentPlanId}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No current plan found for this harbor.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {canAccessSubscriptions && isLoggedInUserHarborAdmin && (
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary contrast:text-white" />{' '}
                Harbor Subscription Plans
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {associatedClubs.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">
                    {subscriptionMessage ||
                      'This harbor’s current subscription is associated with the following club(s)'}
                  </p>
                  <ul className="list-disc list-inside text-sm text-blue-800">
                    {associatedClubs.map((club, index) => (
                      <li key={index}>{club}</li>
                    ))}
                  </ul>
                </div>
              )}
              <SubscriptionPlansCard
                subscriptions={subscriptions}
                loading={subscriptionsLoading}
                currentPlanId={currentPlanId}
                onUpgrade={handleUpgrade}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Helper component for detail items
const DetailItem: React.FC<{
  icon: React.ElementType;
  label: string;
  value?: string | number | null;
  isLink?: boolean | string;
}> = ({ icon: Icon, label, value, isLink = false }) => {
  if (!value) return null;

  const content =
    isLink && typeof value === 'string' ? (
      <a
        href={typeof isLink === 'string' ? isLink : value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline contrast:!text-blue-400"
      >
        {value}
      </a>
    ) : (
      <span className="text-muted-foreground contrast:!text-white">
        {value}
      </span>
    );

  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <div className="flex flex-col">
        <span className="text-sm font-medium text-foreground contrast:!text-white">
          {label}
        </span>
        <div className="text-sm break-all ">{content}</div>
      </div>
    </div>
  );
};
